import type { FilterSchema } from '@/shared/components/filter/validation'
import type { WidgetPeriods } from '../types'

import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { filter, isNil, map, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useAiBIAnalyticQuery, useMetricsQuery } from '@/entities/bi'

import { SalesFactorsImpactChart } from '@/widgets/sales-factors-impact'

import { ampli } from '@/shared/lib/ampli'

import { AiDashboardFilter } from '../../Filter'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'
import WidgetLoadingState from '../../WidgetLoadingState'

export default function SalesFactorsImpactWidget({
	commonFilters,
	commonPeriods,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const {
		data: metrics,
		isLoading: isMetricsLoading,
		isError: isMetricsError,
	} = useMetricsQuery<string[]>({
		select: (data) => map('name', filter({ type: 'ai_importance' }, data)),
	})
	const commonQueryParams = {
		label: 'SalesFactorsImpact',
		...(commonPeriods?.main || {
			start_date: null,
			end_date: null,
		}),
		skip_dates: true,
		metrics: metrics as string[],
		dimensions: [],
		date_aggregation: null,
		filters: [...commonFilters, ...(params.filters ?? [])],
	}
	const {
		data: biAnalytic,
		isLoading: isBiAnalyticLoading,
		isError: isBiAnalyticError,
	} = useAiBIAnalyticQuery(commonQueryParams, {
		enabled: !!metrics?.length,
	})
	const chartData = (biAnalytic?.data?.[0] || []).map((value, i) => ({
		metric: metrics![i],
		value,
	}))
	const isEmpty = biAnalytic?.data?.[0].every(isNil)
	const isLoading = isMetricsLoading || isBiAnalyticLoading
	const isError = isMetricsError || isBiAnalyticError

	return (
		<Widget className='relative flex flex-auto flex-col'>
			<WidgetLoadingState isTinted={isLoading} />
			<WidgetErrorState isTinted={isError} />
			<WidgetHeader title={t('widget.sales_factors_impact.title')}>
				<AiDashboardFilter
					filters={params.filters ?? []}
					onChange={(filters) => {
						ampli.dashboardBiApplyFilterSalesFactorsImpact({
							'filter by': filters.map(prop('name')).join(','),
						})
						onChangeParams({ ...params, filters })
					}}
				/>
			</WidgetHeader>
			<WidgetContent className='space-y-3 pb-0'>
				{isEmpty ? (
					<WidgetEmptyState />
				) : (
					<SalesFactorsImpactChart data={chartData} height={390} />
				)}
			</WidgetContent>
		</Widget>
	)
}
